import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import ProfileAlertButton from '@modules/alerts/components/ProfileAlertButton'
import { ProfileAlert } from '@modules/alerts/types'
import { CompanyAssetHistoryProfile, ProfileType } from '@modules/search/types'
import ShortlistProfileButton from '@modules/shortlist/components/ShortlistProfileButton'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'

import CompanyCard from '../CompanyCard'
import { EventInfo } from '../EventInfo'
import ProfileDetails from '../ProfileDetails'
import ProfileEvents from '../ProfileEvents'
import useFetchProfile from '../hooks/useFetchProfile'
import {
  ProfileContent,
  ProfileContentWrapper,
  ProfileMainColumn,
  ProfileName,
  ProfileOtherInfo,
  ProfileOuterWrapper,
  ProfileSkeleton,
  ProfileTitleButtons,
  ProfileTitleInner,
  ProfileTitleWrapper,
  ProfileWrapper,
  ProfileWrapperInner,
} from '../index.styles'

interface AssetProfileProps {
  profile?: CompanyAssetHistoryProfile
  isError: boolean
  isFetching: boolean
  profileAlert?: ProfileAlert
}

const AssetProfile = (props: AssetProfileProps) => {
  const { profile, isError, isFetching, profileAlert } = props
  const { isDesktop } = useScreenDimensions()
  const profileData = profile?.latest

  const isPrivate = useMemo(
    () => profileData?.isPrivate && !profile?.events?.find(event => !event.hidden),
    [profile?.events, profileData?.isPrivate],
  )

  const { t } = useTranslation()
  const { hasAccessToAlerts } = useFeatureFlags()
  const { data: fullCompanyProfile } = useFetchProfile({ type: ProfileType.company, id: profileData?.companyId, disabled: true })

  return (
    <ProfileOuterWrapper>
      <ProfileWrapper>
        {isError && !isFetching && (
          <ProfileWrapperInner>
            <ProfileContentWrapper isError>{t(translationKeys.FETCHING_ERROR)}</ProfileContentWrapper>
          </ProfileWrapperInner>
        )}
        {isFetching && (
          <ProfileWrapperInner>
            <ProfileContentWrapper isFetching>
              <ProfileContent>
                <ProfileMainColumn>
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                </ProfileMainColumn>
                <ProfileOtherInfo>
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                </ProfileOtherInfo>
              </ProfileContent>
            </ProfileContentWrapper>
          </ProfileWrapperInner>
        )}
        {profileData && !isFetching && (
          <>
            <ProfileWrapperInner>
              <ProfileContentWrapper>
                <ProfileTitleWrapper>
                  <ProfileTitleInner>
                    <ProfileName variant="h2">{profileData?.title}</ProfileName>
                    <EventInfo updatedAt={profileData?.updatedAt} />
                  </ProfileTitleInner>
                  <ProfileTitleButtons>
                    {hasAccessToAlerts && (
                      <ProfileAlertButton
                        profile={profile}
                        withLastViewedUpdate
                        savedProfileAlert={profileAlert}
                        isPrivate={isPrivate}
                      />
                    )}
                    <ShortlistProfileButton profile={profileData} events={profile.events} location="asset_profile" />
                  </ProfileTitleButtons>
                </ProfileTitleWrapper>
                <ProfileContent>
                  <ProfileMainColumn>
                    <ProfileDetails
                      profileData={profileData}
                      profileType={profileData['@type']}
                      profileAreaName="mainInfo"
                      profileAlert={profileAlert}
                    />
                  </ProfileMainColumn>
                  <ProfileOtherInfo>
                    {!!fullCompanyProfile?.events?.length && (
                      <ProfileEvents
                        events={fullCompanyProfile.events}
                        name={fullCompanyProfile?.latest.name}
                        lastViewedAt={profileAlert?.lastViewedAt}
                      />
                    )}
                    <ProfileDetails
                      profileData={profileData}
                      profileType={profileData['@type']}
                      profileAreaName="secondColumnInfo"
                      profileAlert={profileAlert}
                    />
                  </ProfileOtherInfo>
                </ProfileContent>
              </ProfileContentWrapper>
            </ProfileWrapperInner>
            {isDesktop && <CompanyCard companyId={profileData?.companyId} />}
          </>
        )}
      </ProfileWrapper>
    </ProfileOuterWrapper>
  )
}

export default AssetProfile
